import * as React from "react";
import {
  Box,
  Paper,
  Stack,
  Typography,
  Button,
  IconButton,
  LinearProgress,
  Alert,
  Chip,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import StarIcon from "@mui/icons-material/Star";
import { adminFetch } from "../../services/adminFetch";

type ImgRow = {
  id: number;
  property_id: number;
  image_url: string;      // normalized absolute (backend)
  image_url_raw: string;  // "/uploads/..."
  alt_text?: string | null;
  sort_order?: number | null;
};

type Props = {
  propertyId: number;
  featuredImageUrl?: string | null;          // από property_to_row
  onFeaturedChange?: (url: string) => void;  // ενημερώνει parent
  onItemsChange?: (items: ImgRow[]) => void;
};

export default function AdminImagesPanel({
  propertyId,
  featuredImageUrl,
  onFeaturedChange,
  onItemsChange,
}: Props) {
  const [items, setItems] = React.useState<ImgRow[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [uploading, setUploading] = React.useState(false);
  const [progress, setProgress] = React.useState<number | null>(null);
  const [err, setErr] = React.useState<string | null>(null);
  const [ok, setOk] = React.useState<string | null>(null);

  const load = React.useCallback(async () => {
    if (!propertyId) return;
    setLoading(true);
    setErr(null);
    try {
      const res = await adminFetch(`/api/admin/properties/${propertyId}/images`);
      const data = await res.json().catch(() => ({}));
      const nextItems = Array.isArray(data.items) ? data.items : [];
      setItems(nextItems);
      onItemsChange?.(nextItems);
    } catch {
      setErr("Αποτυχία φόρτωσης εικόνων.");
      setItems([]);
      onItemsChange?.([]);
    } finally {
      setLoading(false);
    }
  }, [propertyId, onItemsChange]);

  React.useEffect(() => {
    load();
  }, [load]);

  // ✅ 1-step: upload μέσω Flask -> Plesk -> DB
  async function uploadViaBackend(file: File) {
    const form = new FormData();
    form.append("file", file);

    const res = await adminFetch(`/api/admin/properties/${propertyId}/images/upload`, {
      method: "POST",
      body: form,
    });

    const data = await res.json().catch(() => ({}));
    if (!res.ok || !data?.ok) {
      throw new Error(data?.error || "Upload failed");
    }

    // Βρες raw path από οποιοδήποτε shape επιστρέφει το upload endpoint
    const raw =
      (data?.item?.image_url_raw) ||
      (data?.item?.image_url) ||
      (data?.image_url_raw) ||
      (data?.image_url) ||
      (data?.path) ||
      (data?.url) ||
      "";

    if (!raw) throw new Error("Upload ok but no returned path/url");

    return raw as string;
  }

  const onPickFiles = async (files: FileList | null) => {
    if (!files || files.length === 0) return;

    setErr(null);
    setOk(null);
    setUploading(true);
    setProgress(0);

    try {
      for (let i = 0; i < files.length; i++) {
        setProgress(Math.round((i / files.length) * 100));
        const raw = await uploadViaBackend(files[i]);
        const dbRow = await saveImageToDb(raw, null);
        setItems((s) => [dbRow, ...s]);

        // Αν δεν έχει featured ακόμα, βάλε την πρώτη φωτογραφία ως featured (backend το κάνει ήδη)
        if (!featuredImageUrl && i === 0) {
          // θα το πάρουμε από reload (ή από property endpoint)
        }
      }

      setProgress(100);
      setOk("Οι φωτογραφίες ανέβηκαν και αποθηκεύτηκαν.");
      await load();
    } catch (e: any) {
      setErr(e?.message || "Αποτυχία upload.");
    } finally {
      setUploading(false);
      setTimeout(() => setProgress(null), 800);
    }
  };

  function normalizeUploadsRaw(v: string): string {
    const s = (v || "").trim();
    if (!s) return "";

    // αν είναι full URL -> κράτα μόνο από /uploads/ και μετά
    if (s.startsWith("http://") || s.startsWith("https://")) {
      const idx = s.indexOf("/uploads/");
      if (idx !== -1) return s.slice(idx);
      return ""; // δεν είναι δικό μας uploads url
    }

    // αν είναι "uploads/..." -> βάλε leading slash
    if (s.startsWith("uploads/")) return `/${s}`;

    // αν είναι ήδη "/uploads/..." -> ok
    if (s.startsWith("/uploads/")) return s;

    // αν είναι σκέτο filename (π.χ. img_x.webp) -> ΔΕΝ ξέρουμε path -> άκυρο
    return "";
  }

  async function saveImageToDb(rawUrlOrPath: string, alt_text?: string | null) {
    const raw = normalizeUploadsRaw(rawUrlOrPath);

    // ✅ πρόσθεσε αυτό για να δεις τι στέλνεις όταν σκάει
    console.log("[images] saveImageToDb raw input:", rawUrlOrPath, "=> normalized:", raw);

    if (!raw) throw new Error(`Invalid image_url from upload: "${rawUrlOrPath}"`);

    const res = await adminFetch(`/api/admin/properties/${propertyId}/images`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        image_url_raw: raw,          // ΠΑΝΤΑ "/uploads/..."
        alt_text: alt_text || null,
      }),
    });

    const data = await res.json().catch(() => ({}));
    console.log("[images] saveImageToDb response:", res.status, data);

    if (!res.ok || !data?.ok || !data?.item) {
      throw new Error(data?.error || "DB save failed");
    }
    return data.item as ImgRow;
  }

  const deleteImage = async (imgId: number) => {
    setErr(null);
    setOk(null);
    try {
      const res = await adminFetch(`/api/admin/properties/${propertyId}/images/${imgId}`, {
        method: "DELETE",
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data?.ok) {
        setErr(data?.error || "Αποτυχία διαγραφής.");
        return;
      }
      if (Array.isArray(data?.items)) {
        setItems(data.items);
        onItemsChange?.(data.items);
      } else {
        setItems((s) => {
          const next = s.filter((x) => x.id !== imgId);
          onItemsChange?.(next);
          return next;
        });
      }
      if (typeof data?.featured_image_url === "string" && data.featured_image_url) {
        onFeaturedChange?.(data.featured_image_url);
      }
      setOk("Διαγράφηκε.");
    } catch {
      setErr("Αποτυχία διαγραφής.");
    }
  };

  // ✅ featured via dedicated endpoint (does not depend on gallery PUT sync payload)
  const setAsFeatured = async (img: ImgRow) => {
    setErr(null);
    setOk(null);
    try {
      const res = await adminFetch(`/api/admin/properties/${propertyId}/featured-image`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          image_id: img.id, // preferred
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data?.ok || !data?.item?.featured_image_url) {
        setErr(data?.error || "Αποτυχία ορισμού featured.");
        return;
      }
      setOk("Ορίστηκε ως χαρακτηριστική εικόνα.");
      onFeaturedChange?.(String(data.item.featured_image_url));
    } catch {
      setErr("Αποτυχία ορισμού featured.");
    }
  };

  return (
    <Stack spacing={2}>
      {err ? <Alert severity="error">{err}</Alert> : null}
      {ok ? <Alert severity="success">{ok}</Alert> : null}

      <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
        <Stack spacing={0.4}>
          <Typography sx={{ fontWeight: 900 }}>Gallery</Typography>
          {featuredImageUrl ? (
            <Typography variant="body2" color="text.secondary" sx={{ wordBreak: "break-all" }}>
              Featured: {featuredImageUrl}
            </Typography>
          ) : (
            <Typography variant="body2" color="text.secondary">
              Featured: (δεν έχει οριστεί)
            </Typography>
          )}
        </Stack>

        <Button
          component="label"
          variant="contained"
          startIcon={<CloudUploadIcon />}
          disabled={uploading}
          sx={{ fontWeight: 900 }}
        >
          {uploading ? "Uploading..." : "Upload photos"}
          <input
            type="file"
            hidden
            accept="image/jpeg,image/png,image/webp"
            multiple
            onChange={(e) => onPickFiles(e.target.files)}
          />
        </Button>
      </Stack>

      {progress != null ? (
        <Box>
          <Typography variant="body2" color="text.secondary">
            Upload progress: {progress}%
          </Typography>
          <LinearProgress variant="determinate" value={progress} />
        </Box>
      ) : null}

      {loading ? (
        <Typography>Φόρτωση...</Typography>
      ) : items.length === 0 ? (
        <Typography color="text.secondary">Δεν υπάρχουν εικόνες ακόμα.</Typography>
      ) : (
        <Stack spacing={1.5}>
          {items.map((img) => {
            const isFeatured =
              !!featuredImageUrl &&
              (img.image_url === featuredImageUrl || img.image_url_raw === featuredImageUrl);

            return (
              <Paper key={img.id} variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
                <Stack direction={{ xs: "column", md: "row" }} spacing={2} alignItems={{ md: "center" }}>
                  <Box
                    sx={{
                      width: { xs: "100%", md: 220 },
                      height: 140,
                      borderRadius: 2,
                      overflow: "hidden",
                      bgcolor: "action.hover",
                      flex: "0 0 auto",
                    }}
                  >
                    <img
                      src={img.image_url}
                      alt={img.alt_text || ""}
                      style={{ width: "100%", height: "100%", objectFit: "cover" }}
                      loading="lazy"
                    />
                  </Box>

                  <Box sx={{ flex: 1 }}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Typography sx={{ fontWeight: 800 }}>
                        #{img.id} · order {img.sort_order ?? 0}
                      </Typography>
                      {isFeatured ? <Chip size="small" label="FEATURED" icon={<StarIcon />} /> : null}
                    </Stack>
                    <Typography variant="body2" color="text.secondary" sx={{ wordBreak: "break-all" }}>
                      {img.image_url_raw}
                    </Typography>
                  </Box>

                  <Stack direction="row" spacing={1} alignItems="center">
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<StarIcon />}
                      onClick={() => setAsFeatured(img)}
                    >
                      Set featured
                    </Button>

                    <IconButton color="error" onClick={() => deleteImage(img.id)} aria-label="delete">
                      <DeleteIcon />
                    </IconButton>
                  </Stack>
                </Stack>
              </Paper>
            );
          })}
        </Stack>
      )}
    </Stack>
  );
}
